import React, { memo, useMemo, useState, useCallback, useContext, useEffect, useRef } from "react";
import { Package, ChevronDown, Edit, Copy, Tag, Ruler, Box, Hash, Save, X, Loader } from "lucide-react";
import "./BinsTab.css";
import { updateBin, updateSubarea } from "../../services/binService";
import { pixelsToUnits } from "../../../../utils/helpers";
import { Store } from "../../../../store";
import { Spin } from "antd";

const MODES = {
  LIST: "LIST",
  EDIT: "EDIT",
};

const BinsTab = memo(function BinsTab({ bins = [], onCreateBin, type, showBinDirectly, showActions }) {
  const { state, dispatch } = useContext(Store);
  const [mode, setMode] = useState(MODES.LIST);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedBin, setExpandedBin] = useState(null);
  const [editingBin, setEditingBin] = useState(null);
  const [draftBin, setDraftBin] = useState(null);
  const [isApiCalling, setIsApiCalling] = useState(false);

  // Ref for scrolling to specific bin
  const binRefs = useRef({});
  const listContainerRef = useRef(null);
  const searchContainerRef = useRef(null);

  const typeName = type === "bins" ? "Bin" : "Sub-area";

  // Effect to handle showBinDirectly
  useEffect(() => {
    if (showBinDirectly && showBinDirectly.id) {
      const binId = showBinDirectly.id;

      // Find if this bin exists in our current bins list
      const binExists = bins.some((bin) => bin.id === binId);

      if (binExists) {
        // Expand this bin
        setExpandedBin(binId);

        // Scroll to this bin after a small delay to ensure DOM is updated
        setTimeout(() => {
          const binElement = binRefs.current[binId];
          if (binElement && listContainerRef.current) {
            // Get the search container's position
            const searchContainerTop = searchContainerRef.current?.offsetHeight || 0;

            // Calculate position to scroll to
            const container = listContainerRef.current;
            const elementTop = binElement.offsetTop;
            const elementHeight = binElement.offsetHeight;
            const containerHeight = container.clientHeight;

            // Scroll to center the element
            const scrollTop = elementTop - searchContainerTop * 2 - containerHeight / 2 + elementHeight / 2;
            container.scrollTop = scrollTop;

            // Optional: Add highlight effect
            binElement.classList.add("highlighted-bin");
            setTimeout(() => {
              binElement.classList.remove("highlighted-bin");
            }, 2000);
          }
        }, 100);
      }
    }
  }, [showBinDirectly, bins]);

  /* -------------------- LIST MODE LOGIC -------------------- */

  const toggleBin = useCallback((id) => {
    setExpandedBin((prev) => (prev === id ? null : id));
  }, []);

  const filteredBins = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return bins.filter((bin) => bin.name?.toLowerCase().includes(q) || bin.code?.toLowerCase().includes(q));
  }, [searchQuery, bins]);

  /* -------------------- EDIT MODE LOGIC -------------------- */

  const startEdit = useCallback((bin) => {
    setEditingBin(bin);
    setDraftBin({ ...bin }); // clone for safe editing
    setMode(MODES.EDIT);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingBin(null);
    setDraftBin(null);
    setMode(MODES.LIST);
  }, []);

  // save edit
  const saveEdit = useCallback(async () => {
    if (!editingBin || !draftBin) return;

    setIsApiCalling(true);
    const updatedEntity = {
      ...editingBin,
      ...draftBin,
    };

    // convert pixel values to units
    const toUnits = (value) => Number(pixelsToUnits(value, updatedEntity.unitType));

    // configuration per type
    const config = {
      subareas: {
        api: updateSubarea,
        payload: {
          areaId: editingBin.areaId,
          subareaId: editingBin.id,
          subareaName: updatedEntity.name,
          subareaCode: updatedEntity.code,
          width: toUnits(updatedEntity.width),
          height: toUnits(updatedEntity.height),
          maxCapacity: Number(updatedEntity.maxCapacity),
        },
        updateStore: () =>
          dispatch({
            type: "UPDATE_SUBAREAS",
            payload: {
              id: editingBin.areaId,
              subAreas: bins.map((sa) => (sa.id === editingBin.id ? updatedEntity : sa)),
            },
          }),
        idKey: "subareaId",
      },

      bins: {
        api: updateBin,
        payload: {
          rackId: editingBin.rackId,
          binId: editingBin.id,
          binName: updatedEntity.name,
          binCode: updatedEntity.code,
          width: toUnits(updatedEntity.width),
          height: toUnits(updatedEntity.height),
          maxCapacity: Number(updatedEntity.maxCapacity),
        },
        updateStore: () =>
          dispatch({
            type: "UPDATE_BINS",
            payload: {
              id: editingBin.rackId,
              bins: bins.map((bin) => (bin.id === editingBin.id ? updatedEntity : bin)),
            },
          }),
        idKey: "binId",
      },
    };

    const current = config[type];
    if (!current) return;

    try {
      const response = await current.api(current.payload[current.idKey], current.payload);

      if (response?.data?.success) {
        current.updateStore();
      }
    } catch (error) {
      console.error("Save edit failed:", error);
    } finally {
      setEditingBin(null);
      setDraftBin(null);
      setMode(MODES.LIST);
      setIsApiCalling(false);
    }
  }, [draftBin, editingBin, type, bins, dispatch]);

  const updateDraft = (key, value) => {
    setDraftBin((prev) => ({ ...prev, [key]: value }));
  };

  /* ==================== EDIT MODE UI ==================== */

  if (mode === MODES.EDIT && draftBin) {
    return (
      <div className="bins-tab edit-mode">
        <div className="edit-header">
          <h3>Edit {typeName}</h3>
        </div>

        <div className="edit-form">
          <label>
            Code
            <input value={draftBin.code} onChange={(e) => updateDraft("code", e.target.value)} />
          </label>

          <label>
            Width
            <input type="number" value={draftBin.width} onChange={(e) => updateDraft("width", e.target.value)} />
          </label>

          <label>
            Height
            <input type="number" value={draftBin.height} onChange={(e) => updateDraft("height", e.target.value)} />
          </label>

          <label>
            Max Capacity
            <input type="number" value={draftBin.maxCapacity || ""} onChange={(e) => updateDraft("maxCapacity", e.target.value)} />
          </label>
        </div>

        <div className="edit-actions">
          <button className="cancel-btn" onClick={cancelEdit}>
            <X size={16} /> Cancel
          </button>
          <button className="save-btn" onClick={saveEdit} disabled={isApiCalling}>
            {isApiCalling ? (
              <>
                <Spin size="small" className="white-spinner" style={{ marginRight: 8 }} />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  /* ==================== LIST MODE UI ==================== */

  return (
    <div className="bins-tab">
      {/* Search */}
      <div className="search-container" ref={searchContainerRef}>
        <Package className="search-icon" />
        <input
          type="text"
          placeholder={`Search ${typeName}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Bins List */}
      <div className="bins-list-container" ref={listContainerRef}>
        {filteredBins.length === 0 ? (
          <div className="empty-state">
            <Package className="empty-icon" />
            <p>No {typeName} found</p>
            {showActions && (
              <button className="create-bin-btn" onClick={onCreateBin}>
                Create {typeName}
              </button>
            )}
          </div>
        ) : (
          filteredBins.map((bin) => (
            <div key={bin.id} className={`bin-item ${expandedBin === bin.id ? "expanded" : ""}`} ref={(el) => (binRefs.current[bin.id] = el)}>
              <button className="bin-header" onClick={() => toggleBin(bin.id)}>
                <div className="bin-info">
                  <div className="bin-color" style={{ backgroundColor: `${bin.color}30` }}>
                    <Package className="bin-icon" style={{ color: bin.color }} />
                  </div>
                  <span className="bin-name">{bin.code}</span>
                </div>
                <ChevronDown className={`chevron ${expandedBin === bin.id ? "rotate" : ""}`} />
              </button>

              {expandedBin === bin.id && (
                <div className="bin-expanded">
                  <div className="bin-details-grid">
                    <DetailRow icon={Hash} label={`${typeName} ID`} value={bin.id} />
                    <DetailRow icon={Tag} label="Code" value={bin.code} />
                    <DetailRow icon={Ruler} label="Dimensions" value={`${bin.width} × ${bin.height} ${bin.unitType}`} />
                    <DetailRow icon={Box} label="Max Capacity" value={bin.maxCapacity || "N/A"} />
                  </div>

                  {showActions && (
                    <div className="bin-buttons">
                      <button className="edit-btn" onClick={() => startEdit(bin)}>
                        <Edit size={16} /> Edit
                      </button>
                      {/* <button className="copy-btn">
                      <Copy size={16} /> Duplicate
                    </button> */}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
});

export default BinsTab;

/* -------------------- Reusable Row -------------------- */

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="location-detail-row">
    <Icon className="detail-icon" />
    <div>
      <p className="detail-label">{label}</p>
      <p className="detail-value">{value}</p>
    </div>
  </div>
);
